import { useContext, useEffect } from "react";
import "../App.css";
import { AppContext } from "../context/AppContext";
import { Box, Button, Card, Grid2, IconButton, Tooltip, Typography } from "@mui/material";
import OwnerAdjustments from "../components/OwnerAdjustments";
import AgentContactCard from "../components/AgentContactCard";
import AgentMessageCard from "../components/AgentMessageCard";
import ValueTrendCard from "../components/ValueTrendCard";
import DisclaimerCard from "../components/DisclaimerCard";
import PropertyScroller from "../components/PropertyScroller";
import { formatAsPercentage, formatCmpctNumber } from "../helpers/utils";
import { CardId } from "../interfaces/AppDefinitions";
import {
	ArrowDownward,
	ArrowUpward,
	CurrencyExchangeOutlined,
	Info,
	LocationCity,
	TrendingDown,
	TrendingUp,
} from "@mui/icons-material";
import { OwnerConsoleViewController } from "../controllers/OwnerConsoleViewController";
import ModernReport from "../components/modern/ModernReport";
import ReportLoader from "../components/ReportLoader";
import AssistantChat from "../components/assistant/AssistantChat";
import EquityPanel from "../components/mortgage/EquityPanel";
import { MortgageProvider, useMortgage } from "../components/mortgage/MortgageContext";

function ReportPage() {
	const { appState } = useContext(AppContext);
	const { report_configuration, currentAddrId, info, appInitialized, showErrorPage } = appState;
	var currentReport = report_configuration?.find((_) => _?.addrId === currentAddrId);
	useEffect(() => {
		if (info.client?.name) {
			document.title = `${info.client?.name} - Owner Console Report from ${info.agent?.agentName}`;
		}
	}, [info.client?.name, info.agent?.agentName]);
	if (showErrorPage) {
		return (
			<Box sx={{ width: "100%", textAlign: "center", height: "500px", alignContent: "center" }}>
				<Typography variant="h3">Sorry, this report does not exist.</Typography>
				<Typography variant="h4">Please contact your agent for further assistance.</Typography>
			</Box>
		);
	}
	if (!appInitialized) {
		return <ReportLoader />;
	}
	if (info.agent.reportLayout === "MODERN") {
		return <ModernReport />;
	}
	return (
		<Grid2 container spacing={1.5} padding={0}>
			<Grid2 size={12} sx={{ maxWidth: { sm: 550, md: 700, lg: 1200, xl: 1500 }, minWidth: { md: 900 } }}>
				<PropertyScroller />
			</Grid2>
			<AgentMessageCard />
			<Grid2 container size={{ xs: 12, md: 9 }}>
				<ValuationCard
					id={CardId.CURRENTVALUE}
					label="Current Home Value"
					value={currentReport?.reportData?.estimatedValue}
					icon={
						<Box
							sx={{
								boxShadow: "0px 0px 10px 2px rgba(22, 179, 128, 0.5)",
								color: "#16B380",
								borderRadius: "7px",
								paddingLeft: "4px",
								paddingRight: "4px",
								paddingTop: "6px",
								backgroundColor: "#E2FBEB",
							}}
						>
							<LocationCity />
						</Box>
					}
				/>
				{info.agent.displayValuationRange && (
					<>
						<ValuationCard
							id={CardId.UPPERRANGE}
							label="Upper Range"
							value={currentReport?.reportData.upperRange}
							icon={
								<Box
									sx={{
										boxShadow: "0px 0px 10px 2px rgba(22, 179, 128, 0.5)",
										color: "#16B380",
										borderRadius: "7px",
										paddingLeft: "4px",
										paddingRight: "4px",
										paddingTop: "6px",
										backgroundColor: "#E2FBEB",
									}}
								>
									{<ArrowUpward />}
								</Box>
							}
						/>
						<ValuationCard
							id={CardId.LOWERRANGE}
							label="Lower Range"
							value={currentReport?.reportData.lowerRange}
							icon={
								<Box
									sx={{
										boxShadow: "0px 0px 10px 2px rgba(252, 60, 80, 0.5)",
										color: "#fc3c50",
										borderRadius: "7px",
										paddingLeft: "4px",
										paddingRight: "4px",
										paddingTop: "6px",
										backgroundColor: "#fceaea",
									}}
								>
									{<ArrowDownward />}
								</Box>
							}
						/>
					</>
				)}
				<MonthlyChange
					monthlyChange={currentReport?.reportData.monthlyChange}
					show={info.agent.displayMonthlyChange === true}
				/>
				<GainLoss gainLoss={currentReport?.reportData.gainLoss} show={info.agent.displayGainLoss === true} />
				<ROI annualROI={currentReport?.reportData.annualROI} show={info.agent.displayAnnualROI === true} />
				<ClassicEquity estimatedValue={currentReport?.reportData?.estimatedValue} />
				<MortgageCard id={CardId.MORTGAGE} />
				<AgentContactCard mobileView />
				<OwnerAdjustments mobileView />
				<ValueTrendCard />
				{info.aiAssistant?.enabled && (
					<Grid2 size={12}>
						<AssistantChat />
					</Grid2>
				)}
			</Grid2>

			<Grid2 container size={{ xs: 12, sm: 12, md: 3 }}>
				<AgentContactCard />
				<OwnerAdjustments />
				<DisclaimerCard />
			</Grid2>
		</Grid2>
	);
}
// Mortgage Insights equity card, full width under the value cards.
function ClassicEquity(props: { estimatedValue: number | undefined }) {
	const mortgage = useMortgage();
	if (!mortgage?.enabled || !mortgage.address || props.estimatedValue === undefined) return null;
	return (
		<Grid2 size={12}>
			<EquityPanel variant="classic" estimatedValue={props.estimatedValue} />
		</Grid2>
	);
}
function MonthlyChange(props: { monthlyChange: number | undefined; show: boolean }) {
	if (props.show === false || props.show === undefined || props.monthlyChange === undefined) return null;
	return (
		<ValuationCard
			id={CardId.MONTHLYCHANGE}
			label="Monthly Change"
			value={props.monthlyChange}
			icon={
				<Box
					sx={{
						boxShadow:
							props.monthlyChange > 0
								? "0px 0px 10px 2px rgba(22, 179, 128, 0.5)"
								: "0px 0px 10px 2px rgba(252, 60, 80, 0.5)",
						color: props.monthlyChange > 0 ? "#16B380" : "#fc3c50",
						borderRadius: "7px",
						paddingLeft: "4px",
						paddingRight: "4px",
						paddingTop: "6px",
						backgroundColor: props.monthlyChange > 0 ? "#E2FBEB" : "#fceaea",
					}}
				>
					{props.monthlyChange > 0 ? <TrendingUp /> : <TrendingDown />}
				</Box>
			}
		/>
	);
}

function GainLoss(props: { gainLoss: number | undefined; show: boolean }) {
	if (props.show === false || props.show === undefined || props.gainLoss === undefined || props.gainLoss === 0)
		return null;
	return (
		<ValuationCard
			infoTooltip={"The difference between the current estimated value and the purchase price"}
			id={CardId.GAINLOSS}
			label="Gain/Loss"
			value={props.gainLoss}
			icon={
				<Box
					sx={{
						color: props.gainLoss > 0 ? "#16B380" : "#fc3c50",
						borderRadius: "7px",
						paddingLeft: "4px",
						paddingRight: "4px",
						paddingTop: "6px",
						backgroundColor: props.gainLoss > 0 ? "#E2FBEB" : "#fceaea",
						boxShadow:
							props.gainLoss > 0
								? "0px 0px 10px 2px rgba(22, 179, 128, 0.5)"
								: "0px 0px 10px 2px rgba(252, 60, 80, 0.5)",
					}}
				>
					{props.gainLoss > 0 ? <TrendingUp /> : <TrendingDown />}
				</Box>
			}
		/>
	);
}
function ROI(props: { annualROI: number | undefined; show: boolean }) {
	const { appState, appDispatch } = useContext(AppContext);
	if (props.show === false || props.show === undefined || props.annualROI === undefined || props.annualROI === 0)
		return null;
	const cardId = CardId.ROI;
	const mainCard = cardId === appState.selectedCard;
	const label = "ROI";
	const value = props.annualROI;
	return (
		<Grid2 size={{ xs: 12, md: 4 }} sx={{ alignContent: "left" }}>
			<Card
				component={"div"}
				onClick={() => {
					if (mainCard) {
						appDispatch({ type: "SET_SELECTED_CARD", id: CardId.NONE });
					} else {
						appDispatch({ type: "SET_SELECTED_CARD", id: cardId });
					}
				}}
				sx={{
					transition: "all .5s ease",
					zoom: mainCard ? 1.01 : 0,
					cursor: "pointer",
					background: mainCard ? appState.colorPallete["scheme1"] : "",
					color: mainCard ? "white" : "",
					// minHeight: "120px",
				}}
			>
				<Grid2 container>
					<Grid2 size={2} pr={1.5} sx={{ textAlign: "right", alignContent: "center" }}>
						<Button
							sx={{
								pointerEvents: "none",
							}}
						>
							<Box
								sx={{
									color: value > 0 ? "#16B380" : "#fc3c50",
									backgroundColor: value > 0 ? "#E2FBEB" : "#fceaea",
									borderRadius: "7px",
									paddingLeft: "4px",
									paddingRight: "4px",
									paddingTop: "6px",
									boxShadow:
										value > 0
											? "0px 0px 10px 2px rgba(22, 179, 128, 0.5)"
											: "0px 0px 10px 2px rgba(252, 60, 80, 0.5)",
								}}
							>
								<CurrencyExchangeOutlined />
							</Box>
						</Button>
					</Grid2>
					<Grid2 size={10} p={2} sx={{ textAlign: "left", alignContent: "left" }}>
						<Grid2 size={{ xs: 12, md: 12 }} pb={2.5}>
							<Typography
								variant="body1"
								sx={{ textAlign: "left", fontWeight: 500, fontSize: "18px", lineHeight: "1px" }}
							>
								{label}
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 12 }}>
							<Typography variant="h4" sx={{ fontWeight: 700, lineHeight: "20px" }}>
								{formatAsPercentage(value)} &nbsp;
								<Tooltip title="Annual return on investment since purchase">
									<Info fontSize="small" />
								</Tooltip>
							</Typography>
						</Grid2>
					</Grid2>
				</Grid2>
			</Card>
		</Grid2>
	);
}
function ValuationCard(props: {
	label: string;
	value: number | undefined;
	icon: React.ReactNode;
	id: CardId;
	infoTooltip?: string;
}) {
	const { label, value, icon } = props;
	const { appState, appDispatch } = useContext(AppContext);

	const mainCard = props.id === appState.selectedCard;
	return (
		<Grid2 size={{ xs: 12, md: 4 }} sx={{ alignContent: "left" }}>
			<Card
				component={"div"}
				onClick={() => {
					if (mainCard) {
						appDispatch({ type: "SET_SELECTED_CARD", id: CardId.NONE });
					} else {
						appDispatch({ type: "SET_SELECTED_CARD", id: props.id });
					}
				}}
				sx={{
					transition: "all .5s ease",
					zoom: mainCard ? 1.01 : 0,
					cursor: "pointer",
					background: mainCard ? appState.colorPallete["scheme1"] : "",
					color: mainCard ? "white" : "",
					// minHeight: "120px",
				}}
			>
				<Grid2 container>
					<Grid2 size={2} pr={1.5} sx={{ textAlign: "right", alignContent: "center" }}>
						<Button
							sx={{
								pointerEvents: "none",
							}}
						>
							{icon}
						</Button>
					</Grid2>
					<Grid2 size={10} p={2} sx={{ textAlign: "left", alignContent: "left" }}>
						<Grid2 size={{ xs: 12, md: 12 }} pb={2.5}>
							<Typography
								variant="body1"
								sx={{ textAlign: "left", fontWeight: 500, fontSize: "18px", lineHeight: "1px" }}
							>
								{label}
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 12 }}>
							<Typography variant="h4" sx={{ fontWeight: 700, lineHeight: "20px" }}>
								{formatCmpctNumber(value ?? 0)} &nbsp;
								{props.infoTooltip && (
									<Tooltip title={props.infoTooltip}>
										<Info fontSize="small" />
									</Tooltip>
								)}
							</Typography>
						</Grid2>
					</Grid2>
				</Grid2>
			</Card>
		</Grid2>
	);
}

function MortgageCard(props: { id: CardId; infoTooltip?: string }) {
	const { appState, appDispatch } = useContext(AppContext);
	const { info } = appState;
	if (info.mortgage === undefined || !info.agent.customMortgageProvider) return null;
	return (
		<Grid2 size={{ xs: 12, md: 12, lg: 12, sm: 12 }} sx={{}}>
			<Card
				component={"div"}
				sx={{
					padding: 1,
					backgroundColor: appState.colorPallete["scheme2"],
					textAlign: "center",
					transition: "all .5s ease",
				}}
			>
				<Grid2 container p={1.5} sx={{ textAlign: "center", alignContent: "center" }}>
					<Grid2 size={12}>
						<Typography variant="h4" sx={{}}>
							{info.agent.mortgageRateText || ""}
							{/* Lowest {info.mortgage.term} year fixed rate:
							<b>{formatAsPercentage(info.mortgage.rate / 100)} &nbsp;</b> */}
							{props.infoTooltip && (
								<Tooltip title={props.infoTooltip}>
									<Info fontSize="small" />
								</Tooltip>
							)}
						</Typography>
					</Grid2>
					<Grid2 size={12} sx={{ p: 1 }}>
						<IconButton
							sx={{
								height: 50,
								width: 290,
								color: "white",
								background: appState.colorPallete["scheme1"],
								borderRadius: "8px",
								"&:hover": {
									background: appState.colorPallete["scheme1"],
									zoom: 1.05,
								},
							}}
							onClick={async () => {
								try {
									await OwnerConsoleViewController.TrackMortgageClick({ enc: appState.enc });
								} catch (err) {
									console.log(err);
								}
								var link = `${info.agent.customMortgageProvider}?enc=${appState.enc}`;
								window.open(link, "width=600,height=600");
							}}
						>
							<ArrowUpward sx={{ height: 20, width: 20, rotate: "45deg" }} />
							<Typography sx={{ pl: 1, fontSize: "20px", fontWeight: 700 }} variant="caption">
								{info.agent.mortgageButtonText || "See Mortgage Options"}
							</Typography>
						</IconButton>
					</Grid2>
				</Grid2>
			</Card>
		</Grid2>
	);
}
function ReportContainer() {
	return (
		<div className="App">
			<MortgageProvider>
				<ReportPage />
			</MortgageProvider>
		</div>
	);
}

export default ReportContainer;
